/**
 * Human-readable timestamps.
 *
 * Both are used by more than one dashboard page, so they live here rather than
 * being exported from a `page.tsx` — the App Router treats a page module's
 * exports as its own configuration surface, and adding helpers to it invites a
 * name collision with a future framework export.
 *
 * The pattern throughout the dashboard is `relativeTime` as the visible text and
 * `absoluteDate` as the `title`. Relative alone ("3 months ago") is what someone
 * wants at a glance; absolute is what they need when the answer actually matters,
 * such as recognising whether a session predates a password change.
 */

/** "3 days ago" / "in 4 weeks" / "just now". `—` when there is no timestamp. */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'

  const seconds = Math.round((then - Date.now()) / 1000)
  const abs = Math.abs(seconds)

  // Largest unit that the gap actually fills, so nothing reads "90 days ago".
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ]
  const fmt = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  for (const [unit, size] of units) {
    if (abs >= size) return fmt.format(Math.round(seconds / size), unit)
  }
  return 'just now'
}

/**
 * The unambiguous form, for a `title` attribute.
 *
 * UTC deliberately: these pages are server-rendered, so formatting in the
 * server's local zone would show a time that is wrong for most readers and — worse
 * — would differ between server and client, which React reports as a hydration
 * mismatch. UTC is the same everywhere and is explicitly labelled as such.
 */
export function absoluteDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toUTCString()
}
